// Multi-tenancy client lib. Wraps the orgs / memberships / invites
// queries so the rest of the app reads from a small, stable surface
// instead of hand-rolling Supabase calls in every component.
//
// Phase 1 surface (what's wired up today):
//   - loadCurrentOrg(userId): which org am I a member of, what role?
//   - listMembers(orgId): every member of an org with their role
//   - listInvites(orgId): pending (and recently-revoked) invites
//   - createInvite(orgId, email, role): server-side server route handles
//     token generation + sending the link (TODO Phase 2).
//   - revokeInvite(inviteId)
//
// Phase 1 ASSUMES every user owns exactly one org (the backfill row
// from migration 002). Multi-org membership (joining a teammate's
// agency via invite) is functional at the DB level but the header
// shows only the user's owning org for now.

import { supabase } from './supabase';

// Membership rows for the signed-in user. Returns the owning org first
// (for the header chip default) and any orgs they joined via invite
// after that, with the role on each.
export async function listMyMemberships(userId) {
  if (!userId) return { ok: false, error: 'no user', memberships: [] };
  try {
    const { data, error } = await supabase
      .from('organization_members')
      .select('org_id, role, joined_at, organizations(id, name, plan, seat_limit_agents, seat_limit_vas, owner_user_id, created_at)')
      .eq('user_id', userId);
    if (error) return { ok: false, error: error.message, memberships: [] };
    const memberships = (data || []).map(row => ({
      orgId: row.org_id,
      role:  row.role,
      joinedAt: row.joined_at,
      org: row.organizations || null,
    })).filter(m => m.org)
      // Owner-of-this-org first, then by join date.
      .sort((a, b) => {
        if (a.role === 'owner' && b.role !== 'owner') return -1;
        if (b.role === 'owner' && a.role !== 'owner') return  1;
        return (a.joinedAt || '').localeCompare(b.joinedAt || '');
      });
    return { ok: true, memberships };
  } catch (err) {
    return { ok: false, error: err.message || String(err), memberships: [] };
  }
}

// All members of an org with their role. Used by the team panel to
// render the roster.
export async function listMembers(orgId) {
  if (!orgId) return { ok: false, error: 'no org', members: [] };
  try {
    const { data, error } = await supabase
      .from('organization_members')
      .select('user_id, role, joined_at')
      .eq('org_id', orgId)
      .order('joined_at', { ascending: true });
    if (error) return { ok: false, error: error.message, members: [] };
    return { ok: true, members: data || [] };
  } catch (err) {
    return { ok: false, error: err.message || String(err), members: [] };
  }
}

// Active (not-yet-accepted, not-yet-revoked, not-yet-expired) invites
// for an org. The team panel renders these as "Pending — Resend |
// Revoke" rows next to the active member roster.
export async function listInvites(orgId) {
  if (!orgId) return { ok: false, error: 'no org', invites: [] };
  try {
    const { data, error } = await supabase
      .from('organization_invites')
      .select('id, email, role, token, expires_at, accepted_at, revoked_at, created_at, invited_by')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });
    if (error) return { ok: false, error: error.message, invites: [] };
    return { ok: true, invites: data || [] };
  } catch (err) {
    return { ok: false, error: err.message || String(err), invites: [] };
  }
}

// Create an invite row. Server should generate the token + send the
// email; the client surface here writes through directly for Phase 1
// (the team-panel UI shows the resulting link so the owner can copy
// it manually until the email side is wired up).
export async function createInvite(orgId, email, role) {
  if (!orgId) return { ok: false, error: 'no org' };
  const cleanEmail = (email || '').trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    return { ok: false, error: 'email is required' };
  }
  if (!['admin', 'agent', 'va'].includes(role)) {
    return { ok: false, error: `invalid role "${role}"` };
  }
  // URL-safe token. Length matches the standard Supabase invite token
  // length (32 chars / ~190 bits of entropy).
  const token = randomToken(32);
  try {
    const { data: authData } = await supabase.auth.getUser();
    const invitedBy = authData?.user?.id || null;
    const { data, error } = await supabase
      .from('organization_invites')
      .insert({ org_id: orgId, email: cleanEmail, role, token, invited_by: invitedBy })
      .select()
      .single();
    if (error) return { ok: false, error: error.message };
    return { ok: true, invite: data };
  } catch (err) {
    return { ok: false, error: err.message || String(err) };
  }
}

// Revoke an invite by setting revoked_at. Token stays in the row for
// audit purposes; accept-invite endpoint checks revoked_at and refuses.
export async function revokeInvite(inviteId) {
  if (!inviteId) return { ok: false, error: 'no invite' };
  try {
    const { error } = await supabase
      .from('organization_invites')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', inviteId);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.message || String(err) };
  }
}

// URL-safe random token. crypto.randomUUID() gives 122 bits but with
// dashes; we want a denser alphanum string for invite links.
function randomToken(len = 32) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : null;
  if (cryptoObj && cryptoObj.getRandomValues) {
    const bytes = new Uint8Array(len);
    cryptoObj.getRandomValues(bytes);
    for (let i = 0; i < len; i++) out += alphabet[bytes[i] % alphabet.length];
  } else {
    for (let i = 0; i < len; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}
